import React, { useState, useEffect, useMemo } from 'react'
import { useContract } from '../hooks/useContract'

// AGC: Buff types and their information
// AGC: Using PNG images instead of emojis
const BUFF_TYPES = {
  0: { name: 'Shield', image: '/shield.png', description: 'Reduces incoming damage by 25%' },
  1: { name: 'Pierce', image: '/pierce.png', description: 'Reduces enemy defense effectiveness' },
  2: { name: 'Haste', image: '/haste.png', description: 'Adds +5 flat damage before defense' },
  3: { name: 'Frost', image: '/frost.png', description: 'Freezes opponent for one turn' }
} as const

// AGC: Potion type definitions (matching ClassicPotion1155.sol)
// AGC: Potion image mapping: red=healing, blue=mana, purple=double strike, green=poison
const POTION_TYPES = {
  // AGC: HEALING - Instant energy restore when used
  0: { name: 'Healing Potion', image: '/potion_red.png', description: 'Instant +10 energy when used', color: 'text-red-400' },
  // AGC: MANA - Persistent! Once used, boosts ALL buff activations for rest of battle
  1: { name: 'Mana Potion', image: '/potion_blue.png', description: '+5% buff power for ALL remaining turns', color: 'text-purple-400' },
  // AGC: DOUBLE STRIKE - Conditional! Only activates when you WIN that turn
  2: { name: 'Double Strike Potion', image: '/potion_purple.png', description: '+25% damage ONLY when you win', color: 'text-yellow-400' },
  // AGC: POISON - DoT (Damage over Time) 2.5 dmg × 5 turns = 12.5 total
  3: { name: 'Poison Potion', image: '/potion_green.png', description: '2.5 dmg/turn × 5 turns (12.5 total)', color: 'text-green-400' }
} as const

const RARITY_NAMES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'] as const

export interface BuffSelectorProps {
  playerAddress: string
  onBuffsSelected: (selectedBuffs: number[]) => void
  maxBuffs?: number
  disabled?: boolean
  onNavigateToMarketplace?: () => void  // AGC: Callback to navigate to marketplace
  variant?: 'default' | 'compact'
  allowBothBuffsAndPotions?: boolean  // AGC: If false, selecting one deselects the other (P2P limitation)
  simulationMode?: boolean  // AGC: If true, show 5 of each buff type (simulation mode)
}

export const BuffSelector: React.FC<BuffSelectorProps> = ({ 
  playerAddress, 
  onBuffsSelected, 
  maxBuffs = 2,
  disabled = false,
  onNavigateToMarketplace,  // AGC: Marketplace navigation callback
  variant = 'default',
  allowBothBuffsAndPotions = true,  // AGC: Default to true (PvE allows both), P2P sets to false
  simulationMode = false  // AGC: Simulation mode - show fake balances
}) => {
  // AGC: Contract hooks
  const { getPlayerBuffBalances, canUseBuffToday: _canUseBuffToday, getAvailableBuffs, getPlayerClassicPotions } = useContract()
  
  // AGC: Component state
  const [ownedBuffs, setOwnedBuffs] = useState<{[key: number]: number}>({})
  const [availableBuffs, setAvailableBuffs] = useState<number[]>([])
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [ownedPotions, setOwnedPotions] = useState<Array<{
    tokenId: number
    potionType: number
    rarity: number
    magnitude: number
    balance: number
  }>>([])
  const [selectedPotions, setSelectedPotions] = useState<number[]>([]) // AGC: Selected potion tokenIds (for backward compatibility)
  // AGC: Group potions by type+rarity (like buffs are grouped by type)
  const groupedPotions = useMemo(() => {
    const groups = new Map<string, {
      potionType: number
      rarity: number
      magnitude: number
      totalBalance: number
      tokenIds: number[]
    }>()
    
    ownedPotions.forEach(potion => {
      const key = `${potion.potionType}-${potion.rarity}`
      const existing = groups.get(key)
      if (existing) {
        existing.totalBalance += potion.balance
        existing.tokenIds.push(potion.tokenId)
      } else {
        groups.set(key, {
          potionType: potion.potionType,
          rarity: potion.rarity,
          magnitude: potion.magnitude,
          totalBalance: potion.balance,
          tokenIds: [potion.tokenId]
        })
      }
    })
    
    return Array.from(groups.values())
  }, [ownedPotions])
  const [loading, setLoading] = useState(true)
  const [isDemoMode, setIsDemoMode] = useState(false)
  const [activeTab, setActiveTab] = useState<'CLASSIC' | 'POTIONS' | 'FRIENDS'>('CLASSIC')

  // AGC: Load owned buffs and check daily availability
  useEffect(() => {
    let isMounted = true
    
    const loadBuffData = async () => {
      try {
        setLoading(true)
        console.log('🔍 Loading buff data for:', playerAddress)
        
        if (!playerAddress) return
        
        // AGC: Simulation mode - show 5 of each buff type and 1 of each potion type
        if (simulationMode) {
          console.log('🎮 Simulation mode: Showing 5 of each buff type and 1 of each potion type')
          
          // AGC: Set up simulation buffs
          const simulationBuffs: Record<number, number> = {
            0: 5, // Shield
            1: 5, // Pierce
            2: 5, // Haste
            3: 5  // Frost
          }
          setOwnedBuffs(simulationBuffs)
          setAvailableBuffs([0, 1, 2, 3]) // All buffs available
          setIsDemoMode(false)
          
          // AGC: Set up simulation potions
          const simulationPotions: Array<{
            tokenId: number
            potionType: number
            rarity: number
            magnitude: number
            balance: number
          }> = Object.keys(POTION_TYPES).map((typeIdStr) => {
            const typeId = Number(typeIdStr)
            return {
              tokenId: typeId, // Use typeId as tokenId for simulation
              potionType: typeId,
              rarity: 0, // Common rarity for simulation
              magnitude: 10, // Default magnitude for simulation
              balance: 1 // Always 1 of each potion in simulation
            }
          })
          setOwnedPotions(simulationPotions)
          console.log('✅ Simulation potions loaded:', simulationPotions.length)
          
          setLoading(false)
          return
        }
        
        // AGC: Load owned buff balances
        const ownedBuffsData = await getPlayerBuffBalances(playerAddress)
        if (!isMounted) return
        
        // AGC: Convert array format to Record<number, number> format
        const ownedBuffsRecord: Record<number, number> = {}
        ownedBuffsData.forEach((item: { tokenId: number; balance: number }) => {
          ownedBuffsRecord[item.tokenId] = item.balance
        })
        setOwnedBuffs(ownedBuffsRecord)
        console.log('✅ Owned buffs loaded:', ownedBuffsRecord)
        
        // AGC: Load available buffs (considering daily limits)
        const availableBuffsData = await getAvailableBuffs(playerAddress)
        if (!isMounted) return
        
        console.log('✅ Available buffs loaded:', availableBuffsData)
        
        let demoModeActivated = false
        // AGC: If no buffs are available but we have owned buffs, enable demo mode for testing
        if (availableBuffsData.length === 0 && ownedBuffsData.length > 0) {
          console.log('⚠️ No buffs available today, enabling demo mode for testing...')
          const ownedBuffIds = ownedBuffsData.filter(item => item.balance > 0).map(item => item.tokenId)
          setAvailableBuffs(ownedBuffIds)
          demoModeActivated = true
        } else {
          setAvailableBuffs(availableBuffsData)
        }
        setIsDemoMode(demoModeActivated)
        
        // AGC: Load classic potions (gracefully handle errors - contract might not be deployed yet)
        if (getPlayerClassicPotions) {
          try {
            const potions = await getPlayerClassicPotions(playerAddress)
            if (!isMounted) return
            
            setOwnedPotions(potions)
            if (potions.length > 0) {
              console.log('✅ Classic potions loaded:', potions.length)
            }
          } catch (potionError: any) {
            // AGC: Silently handle potion loading errors - contract might not have new functions yet
            if (potionError?.code !== 'CALL_EXCEPTION') {
              console.warn('⚠️ Failed to load classic potions:', potionError?.message || potionError)
            }
            if (isMounted) {
              setOwnedPotions([])
            }
          }
        }
        
      } catch (error) {
        if (!isMounted) return
        
        console.error('Failed to load buff data:', error)
        
        // AGC: Show empty state if contract calls fail - no demo fallback
        console.log('❌ Contract calls failed, showing empty state')
        setOwnedBuffs({})
        setAvailableBuffs([])
        setOwnedPotions([])
        setIsDemoMode(false)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    
    loadBuffData()
    
    return () => {
      isMounted = false
    }
  }, [playerAddress, simulationMode]) // AGC: Added simulationMode to deps

  // AGC: Handle buff selection
  const handleBuffToggle = (buffId: number) => {
    if (disabled) return
    
    setSelectedBuffs(prev => {
      let newSelected
      
      if (prev.includes(buffId)) {
        // AGC: Remove buff if already selected
        newSelected = prev.filter(id => id !== buffId)
      } else if (prev.length < maxBuffs) {
        // AGC: Add buff if under limit
        newSelected = [...prev, buffId]
      } else {
        // AGC: Replace oldest buff if at limit
        newSelected = [...prev.slice(1), buffId]
      }
      
      // AGC: If P2P mode (allowBothBuffsAndPotions = false), deselect potions when selecting buffs
      if (!allowBothBuffsAndPotions && newSelected.length > 0) {
        setSelectedPotions([])
      }
      
      console.log('🛡️ Buffs selected:', newSelected)
      return newSelected
    })
  }

  // AGC: Handle potion selection (by type+rarity group, like buffs)
  // AGC: Potions allow only 1 selection (not 2 like buffs)
  const handlePotionToggle = (potionType: number, rarity: number) => {
    if (disabled) return
    
    // AGC: Find all tokenIds for this type+rarity group
    const group = groupedPotions.find(g => g.potionType === potionType && g.rarity === rarity)
    if (!group || group.totalBalance === 0) return
    
    // AGC: Use first available tokenId from the group
    const tokenId = group.tokenIds[0]
    
    setSelectedPotions(prev => {
      // AGC: Check if any tokenId from this group is selected
      const isGroupSelected = group.tokenIds.some(id => prev.includes(id))
      
      let newSelected
      if (isGroupSelected) {
        // AGC: Deselect - remove all tokenIds from this group
        newSelected = prev.filter(id => !group.tokenIds.includes(id))
      } else {
        // AGC: Select - potions allow only 1 selection (replace any existing selection)
        newSelected = [tokenId]
        
        // AGC: If P2P mode (allowBothBuffsAndPotions = false), deselect buffs when selecting potion
        if (!allowBothBuffsAndPotions) {
          setSelectedBuffs([])
        }
      }
      
      console.log('🧪 Potions selected:', newSelected)
      return newSelected
    })
  }

  // AGC: Notify parent when selectedBuffs or selectedPotions changes
  useEffect(() => {
    // AGC: Combine buffs and potions for parent callback
    // AGC: Using offset 10000 for potions to distinguish from buffs (0-3)
    // AGC: Buffs: 0-3, Potions: 10000+ (e.g., potion tokenId 1 becomes 10001)
    const combined = [
      ...selectedBuffs,
      ...selectedPotions.map(id => 10000 + id) // AGC: Offset potions by 10000
    ]
    onBuffsSelected(combined)
    // AGC: Removed onBuffsSelected from deps to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuffs, selectedPotions])

  // AGC: Clear all selections
  const handleClearAll = () => {
    if (disabled) return
    setSelectedBuffs([])
    setSelectedPotions([])
    // AGC: onBuffsSelected will be called automatically via useEffect
  }

  if (loading) {
    if (variant === 'compact') {
      return (
      <section className="space-y-1.5 pb-2 border-b border-white/10">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-slate-500">
          <span>🛡️ BUFF LOADOUT</span>
          <span className="text-slate-400">
            {selectedBuffs.length}/{maxBuffs}
          </span>
        </div>
        <p className="text-center text-[9px] text-slate-300">Loading your buffs…</p>
        </section>
      )
    }
    return (
      <div className="bg-slate-800 rounded-lg p-4">
        <h3 className="text-white font-bold mb-3">🛡️ Select Battle Buffs</h3>
        <div className="text-slate-400 text-center py-4">Loading your buffs...</div>
      </div>
    )
  }

  const hasAnyBuffs = Object.values(ownedBuffs).some(count => count > 0)
  const hasAnyPotions = ownedPotions.length > 0

  // AGC: Show empty state only if no buffs AND no potions
  if (!hasAnyBuffs && !hasAnyPotions) {
    if (variant === 'compact') {
      return (
        <section className="space-y-1.5 pb-2 border-b border-white/10 text-center">
          <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-slate-500">
            <span>🛡️ BUFF LOADOUT</span>
            <span className="text-slate-400">
              {selectedBuffs.length}/{maxBuffs}
            </span>
          </div>
          <div className="border border-white/10 px-2 py-2 text-[9px] text-slate-300">
            <div className="mb-1 text-2xl">🛒</div>
            <p>No buffs available. Visit the marketplace to stock up.</p>
          </div>
          {onNavigateToMarketplace && (
            <button
              onClick={onNavigateToMarketplace}
              className="w-full border border-purple-400/40 bg-purple-600/20 py-1.5 text-[10px] font-semibold text-purple-300 transition-colors hover:bg-purple-600/30"
            >
              🛒 Go to Marketplace
            </button>
          )}
        </section>
      )
    }
    return (
      <div className="bg-slate-800 rounded-lg p-4">
        <h3 className="text-white font-bold mb-3">🛡️ Select Battle Buffs</h3>
        <div className="text-slate-400 text-center py-4">
          <div className="text-2xl mb-2">🛒</div>
          <div className="mb-3">No buffs available to use</div>
          <div className="text-sm mb-4">Visit the Marketplace to buy buff NFTs!</div>
          
          {/* AGC: Show marketplace button if callback provided, otherwise continue to battle */}
          {onNavigateToMarketplace ? (
            <button
              onClick={onNavigateToMarketplace}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              🛒 Go to Marketplace
            </button>
          ) : (
            <button
              onClick={() => onBuffsSelected([])}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              ⚔️ Continue to Battle (No Buffs)
            </button>
          )}
        </div>
      </div>
    )
  }

  if (variant === 'compact') {
    // AGC: Get items for current tab
    const getCurrentTabItems = () => {
      if (activeTab === 'CLASSIC') {
        return Object.entries(BUFF_TYPES).map(([buffIdStr, buffInfo]) => ({
          id: parseInt(buffIdStr),
          ...buffInfo,
          type: 'buff' as const
        }))
      } else if (activeTab === 'POTIONS') {
        // AGC: Placeholder for potions - will be implemented later
        return []
      } else if (activeTab === 'FRIENDS') {
        // AGC: Placeholder for friends - will be implemented later
        return []
      }
      return []
    }

    const currentItems = getCurrentTabItems()

    return (
      <section data-tour="battle-buff-selector" className="space-y-1.5 pb-2 border-b border-white/10">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.25em] text-slate-500">
          <span>🛡️ BUFF LOADOUT</span>
          <span className="text-slate-400">
            {selectedBuffs.length}/{maxBuffs}
          </span>
        </div>

        {/* AGC: Tab Navigation - 30% bigger tabs, double size active bar */}
        <div data-tour="battle-category-tabs" className="flex gap-1 border-b border-white/10">
          {(['CLASSIC', 'POTIONS', 'FRIENDS'] as const).map((tab) => (
            <button
              key={tab}
              data-tour={tab === 'POTIONS' ? 'battle-potions-tab' : tab === 'CLASSIC' ? 'battle-classic-tab' : undefined}
              onClick={() => setActiveTab(tab)}
              className={`px-2.5 py-1.5 text-[12px] font-semibold transition-colors border-b-4 ${
                activeTab === tab
                  ? 'border-purple-400/60 text-purple-300'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              {tab === 'CLASSIC' ? 'BUFF NFT' : tab === 'POTIONS' ? 'POTIONS NFT' : tab}
            </button>
          ))}
        </div>

        {isDemoMode && activeTab === 'CLASSIC' && (
          <div className="border border-amber-500/40 px-2 py-1 text-[9px] text-amber-200">
            ⚠️ Demo mode: daily limits bypassed for testing.
          </div>
        )}

        <p className="text-[9px] leading-tight text-slate-300">
          {activeTab === 'CLASSIC' && 'Equip up to 2 buffs. 2nd Buff has more chances if 1st fails.'}
          {activeTab === 'POTIONS' && 'Select 1 potion. Each potion can be used once per battle.'}
          {activeTab === 'FRIENDS' && 'Friends coming soon. Invite friends to join your battles.'}
        </p>

        {/* AGC: Horizontal scrolling - show 4 items per screen (2x2 grid), scroll to see more */}
        {activeTab === 'CLASSIC' && (
          <div
            data-tour="battle-buff-panels"
            className="overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <div className="flex gap-0">
              {/* AGC: Group items into pages of 4 (2x2 grid per page) */}
              {Array.from({ length: Math.ceil(currentItems.length / 4) }).map((_, pageIndex) => {
                const pageItems = currentItems.slice(pageIndex * 4, (pageIndex + 1) * 4)
                
                return (
                  <div
                    key={pageIndex}
                    className="snap-start flex-shrink-0 grid grid-cols-2 gap-1.5"
                    style={{ width: '100%' }}
                  >
                    {pageItems.map((item) => {
                      const buffId = item.id
                      const owned = ownedBuffs[buffId] || 0
                      const isAvailable = availableBuffs.includes(buffId)
                      const isSelected = selectedBuffs.includes(buffId)
                      const canSelect = owned > 0 && isAvailable && !disabled

                      return (
                        <button
                          key={buffId}
                          data-tour={buffId === 0 ? 'battle-buff-first' : undefined} // AGC: First buff (Shield) for tour step 10
                          onClick={() => handleBuffToggle(buffId)}
                          disabled={!canSelect}
                          className="relative flex flex-col items-center border px-2 py-1.5 text-center transition-all"
                        >
                          {/* AGC: Availability indicator - green checkmark when available, red X when not (only if not selected) */}
                          {owned > 0 && !isSelected && (
                            <div className={`absolute right-1 top-1 w-5 h-5 rounded-full flex items-center justify-center ${
                              isAvailable 
                                ? 'bg-green-500/90 text-white' 
                                : 'bg-red-500/90 text-white'
                            }`}>
                              {isAvailable ? '✓' : '✗'}
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute right-1 top-1 w-5 h-5 rounded-full bg-purple-500/80 flex items-center justify-center text-white">
                              <span className="text-xs font-bold">✓</span>
                            </div>
                          )}
                          {/* AGC: Buff image - 30% bigger (w-8 h-8 = 32px -> 32px * 1.3 = 42px) */}
                          <div className="flex h-14 w-full items-center justify-center">
                            <img src={item.image} alt={item.name} className="w-[42px] h-[42px] object-contain" />
                          </div>
                          {/* AGC: Name: count format at bottom (e.g., "Pierce: 10") */}
                          <p className={`text-[10px] font-semibold leading-tight ${canSelect ? 'text-white' : 'text-slate-500'}`}>
                            {item.name}: {owned}
                          </p>
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* AGC: Potion selection tab (Origami Battle 2.0) */}
        {activeTab === 'POTIONS' && (
          <div data-tour="battle-potions-area" className="space-y-2">
            <p className="text-[9px] text-slate-400 mb-2">Select up to {maxBuffs} potions for battle</p>
            
            <div
              className="overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <div className="flex gap-0">
                {/* AGC: Show empty state in grid area if no potions */}
                {ownedPotions.length === 0 ? (
                  <div className="snap-start flex-shrink-0 grid grid-cols-2 gap-1.5" style={{ width: '100%' }}>
                    <div className="col-span-2 border border-white/10 px-2 py-4 text-center">
                      <div className="text-2xl mb-1">🧪</div>
                      <p className="text-[9px] text-slate-400">No potions available</p>
                      <p className="text-[8px] text-slate-500 mt-1">Visit the marketplace to buy potions</p>
                    </div>
                  </div>
                ) : (
                  /* AGC: Group potions into pages of 4 (2x2 grid per page) - always show 4 slots */
                  Array.from({ length: Math.ceil(Math.max(groupedPotions.length, 1) / 4) }).map((_, pageIndex) => {
                    const pagePotions = groupedPotions.slice(pageIndex * 4, (pageIndex + 1) * 4)
                    // AGC: Always show 4 slots - fill with empty placeholders if needed
                    const slotsToShow = 4
                    const emptySlots = slotsToShow - pagePotions.length
                    
                    return (
                      <div
                        key={pageIndex}
                        className="snap-start flex-shrink-0 grid grid-cols-2 gap-1.5"
                        style={{ width: '100%' }}
                      >
                        {/* AGC: Render actual potions */}
                        {pagePotions.map((group, potionIndex) => {
                          const potionInfo = POTION_TYPES[group.potionType as keyof typeof POTION_TYPES]
                          const rarityName = RARITY_NAMES[group.rarity] || 'Unknown'
                          // AGC: Check if any tokenId from this group is selected
                          const isSelected = group.tokenIds.some(id => selectedPotions.includes(id))
                          const canSelect = group.totalBalance > 0 && !disabled

                          return (
                            <button
                              key={`${group.potionType}-${group.rarity}`}
                              data-tour={pageIndex === 0 && potionIndex === 0 ? 'battle-potion-top-left' : undefined} // AGC: Top left potion for tour step 11
                              onClick={() => handlePotionToggle(group.potionType, group.rarity)}
                              disabled={!canSelect}
                              className="relative flex flex-col items-center border px-2 py-1.5 text-center transition-all"
                            >
                              {/* AGC: Availability indicator - green checkmark when available, red X when not (only if not selected) */}
                              {group.totalBalance > 0 && !isSelected && (
                                <div className={`absolute right-1 top-1 w-5 h-5 rounded-full flex items-center justify-center ${
                                  canSelect 
                                    ? 'bg-green-500/90 text-white' 
                                    : 'bg-red-500/90 text-white'
                                }`}>
                                  {canSelect ? '✓' : '✗'}
                                </div>
                              )}
                              {isSelected && (
                                <div className="absolute right-1 top-1 w-5 h-5 rounded-full bg-purple-500/80 flex items-center justify-center text-white">
                                  <span className="text-xs font-bold">✓</span>
                                </div>
                              )}
                              {/* AGC: Potion image - 30% bigger (w-8 h-8 = 32px -> 32px * 1.3 = 42px) */}
                              <div className="flex h-14 w-full items-center justify-center">
                                <img 
                                  src={potionInfo?.image || '/potion_red.png'} 
                                  alt={potionInfo?.name || 'Potion'}
                                  className="w-[42px] h-[42px] object-contain"
                                />
                              </div>
                              {/* AGC: Name: count format at bottom (e.g., "Healing Potion: 5") */}
                              <p className={`text-[10px] font-semibold leading-tight ${canSelect ? 'text-white' : 'text-slate-500'}`}>
                                {potionInfo?.name || `Potion ${group.potionType}`}: {group.totalBalance}
                              </p>
                            </button>
                          )
                        })}
                        {/* AGC: Render empty placeholder slots to maintain 2x2 grid */}
                        {emptySlots > 0 && Array.from({ length: emptySlots }).map((_, emptyIndex) => (
                          <div
                            key={`empty-${emptyIndex}`}
                            className="relative flex flex-col items-center border border-white/5 px-2 py-1.5 text-center opacity-30"
                          >
                            <div className="flex h-10 w-full items-center justify-center">
                              <span className="text-slate-600 text-2xl">⚗️</span>
                            </div>
                            <p className="text-[10px] font-semibold leading-tight text-slate-600">
                              Empty
                            </p>
                          </div>
                        ))}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* AGC: Placeholder for FRIENDS tab */}
        {activeTab === 'FRIENDS' && (
          <div className="border border-white/10 px-2 py-4 text-center">
            <div className="text-2xl mb-1">👥</div>
            <p className="text-[9px] text-slate-400">Friends coming soon</p>
          </div>
        )}

        {/* AGC: Show selected items for current tab */}
        {activeTab === 'CLASSIC' && selectedBuffs.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedBuffs.map((buffId) => {
              const buffInfo = BUFF_TYPES[buffId as keyof typeof BUFF_TYPES]
              return (
                <div
                  key={buffId}
                  className="flex items-center gap-1 rounded-full bg-purple-600/20 border border-purple-400/40 px-1.5 py-0.5 text-[9px] text-purple-300"
                >
                  <img src={buffInfo.image} alt={buffInfo.name} className="w-4 h-4 object-contain" />
                  <span>{buffInfo.name}</span>
                </div>
              )
            })}
          </div>
        )}

        {activeTab === 'POTIONS' && selectedPotions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedPotions.map((tokenId) => {
              const potion = ownedPotions.find(p => p.tokenId === tokenId)
              const potionInfo = potion ? POTION_TYPES[potion.potionType as keyof typeof POTION_TYPES] : null
              return (
                <div
                  key={tokenId}
                  className="flex items-center gap-1 rounded-full bg-purple-600/20 border border-purple-400/40 px-1.5 py-0.5 text-[9px] text-purple-300"
                >
                  {potionInfo?.image && (
                    <img src={potionInfo.image} alt={potionInfo.name} className="w-3 h-3 object-contain" />
                  )}
                  <span>{potionInfo?.name || `Potion #${tokenId}`}</span>
                </div>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between text-[9px] text-slate-300">
          <button
            type="button"
            onClick={handleClearAll}
            disabled={disabled || (selectedBuffs.length === 0 && selectedPotions.length === 0)}
            className="text-purple-300 transition-colors hover:text-purple-200 disabled:text-slate-600"
          >
            Clear all
          </button>
          <span>
            {activeTab === 'CLASSIC' && (
              selectedBuffs.length === 0
                ? 'Select buffs to enhance your battle'
                : `${selectedBuffs.length}/${maxBuffs} selected`
            )}
            {activeTab === 'POTIONS' && (
              selectedPotions.length === 0
                ? 'Select 1 potion to enhance your battle'
                : '1 potion selected'
            )}
            {activeTab === 'FRIENDS' && 'Friends coming soon'}
          </span>
        </div>
      </section>
    )
  }

  return (
    <div className="bg-slate-800 rounded-lg p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-white font-bold">🛡️ Select Battle Items</h3>
        <div className="text-slate-400 text-sm">
          {activeTab === 'CLASSIC' && `${selectedBuffs.length}/${maxBuffs} selected`}
          {activeTab === 'POTIONS' && `${selectedPotions.length}/${maxBuffs} selected`}
          {activeTab === 'FRIENDS' && '0/0 selected'}
        </div>
      </div>

      {/* AGC: Tab Navigation for non-compact variant */}
      <div className="flex gap-2 mb-4 border-b border-slate-600">
        {(['CLASSIC', 'POTIONS', 'FRIENDS'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
              activeTab === tab
                ? 'border-purple-400 text-purple-300'
                : 'border-transparent text-slate-400 hover:text-slate-300'
            }`}
          >
            {tab === 'CLASSIC' && '🛡️ BUFF NFT'}
            {tab === 'POTIONS' && '🧪 POTIONS NFT'}
            {tab === 'FRIENDS' && '👥 Friends'}
          </button>
        ))}
      </div>
      
      {/* AGC: Demo mode indicator */}
      {availableBuffs.length > 0 && Object.keys(ownedBuffs).length > 0 && (
        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 mb-4">
          <div className="text-yellow-400 text-sm">
            ⚠️ <strong>Demo Mode:</strong> Daily limits bypassed for testing. In production, each buff can only be used once per day.
          </div>
        </div>
      )}
      
      {/* AGC: Daily limits info */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-3">
        <div className="text-blue-400 text-sm font-bold mb-1">📅 Daily Usage Limits</div>
        <div className="text-slate-300 text-xs">
          Each buff can only be used once per day. Buffs reset at midnight UTC.
        </div>
      </div>
      
      {/* AGC: Buff selection grid */}
      {activeTab === 'CLASSIC' && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {Object.entries(BUFF_TYPES).map(([buffIdStr, buffInfo]) => {
            const buffId = parseInt(buffIdStr)
            const owned = ownedBuffs[buffId] || 0
            const isAvailable = availableBuffs.includes(buffId)
            const isSelected = selectedBuffs.includes(buffId)
            const canSelect = owned > 0 && isAvailable && !disabled
            
            return (
              <button
                key={buffId}
                onClick={() => handleBuffToggle(buffId)}
                disabled={!canSelect}
                className={`
                  relative p-3 rounded-lg border-2 transition-all duration-200
                  ${isSelected 
                    ? 'border-purple-500 bg-purple-900/50 shadow-lg shadow-purple-500/25' 
                    : canSelect
                      ? 'border-slate-600 bg-slate-700 hover:border-slate-500 hover:bg-slate-600'
                      : 'border-slate-700 bg-slate-800 opacity-50 cursor-not-allowed'
                  }
                `}
              >
                {/* AGC: Selection indicator */}
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                )}
                
                {/* AGC: Buff info */}
                <div className="text-center">
                  <div className="flex items-center justify-center mb-1">
                    <img src={buffInfo.image} alt={buffInfo.name} className="w-10 h-10 object-contain" />
                  </div>
                  <div className={`font-bold text-sm ${canSelect ? 'text-white' : 'text-slate-500'}`}>
                    {buffInfo.name}
                  </div>
                  <div className={`text-xs ${canSelect ? 'text-slate-300' : 'text-slate-600'}`}>
                    Owned: {owned}
                  </div>
                  {/* AGC: Daily availability status */}
                  {owned > 0 && (
                    <div className={`text-xs mt-1 ${
                      isAvailable 
                        ? 'text-green-400' 
                        : 'text-orange-400'
                    }`}>
                      {isAvailable ? '✅ Available' : '⏰ Used Today'}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* AGC: Potion selection grid */}
      {activeTab === 'POTIONS' && (
        <div className="mb-3">
            <div className="grid grid-cols-2 gap-2">
            {ownedPotions.length === 0 ? (
              <>
                {/* AGC: Show empty state in first slot, keep 2x2 grid */}
                <div className="col-span-2 border border-slate-600 rounded-lg p-6 text-center">
                  <div className="text-4xl mb-2">🧪</div>
                  <p className="text-slate-400 mb-2">No potions available</p>
                  <p className="text-slate-500 text-sm">Visit the marketplace to buy potions</p>
                </div>
                {/* AGC: Empty placeholder slots to maintain 2x2 grid */}
                <div className="border border-slate-700/30 rounded-lg p-3 opacity-30">
                  <div className="flex items-center justify-center h-20">
                    <span className="text-slate-600 text-3xl">⚗️</span>
                  </div>
                </div>
                <div className="border border-slate-700/30 rounded-lg p-3 opacity-30">
                  <div className="flex items-center justify-center h-20">
                    <span className="text-slate-600 text-3xl">⚗️</span>
                  </div>
                </div>
              </>
            ) : (
              (() => {
                // AGC: Always show 4 slots (2x2) - fill with empty placeholders if needed
                const slotsToShow = Math.ceil(groupedPotions.length / 4) * 4
                const emptySlots = slotsToShow - groupedPotions.length
                const allSlots = [...groupedPotions, ...Array(emptySlots).fill(null)]
                
                return allSlots.map((group, index) => {
                  if (!group) {
                    // AGC: Empty placeholder slot
                    return (
                      <div
                        key={`empty-${index}`}
                        className="border border-slate-700/30 rounded-lg p-3 opacity-30"
                      >
                        <div className="flex items-center justify-center h-20">
                          <span className="text-slate-600 text-3xl">⚗️</span>
                        </div>
                      </div>
                    )
                  }
                  
                  const potionInfo = POTION_TYPES[group.potionType as keyof typeof POTION_TYPES]
                  const rarityName = RARITY_NAMES[group.rarity] || 'Unknown'
                  // AGC: Check if any tokenId from this group is selected
                  const isSelected = group.tokenIds.some((id: number) => selectedPotions.includes(id))
                  const canSelect = group.totalBalance > 0 && !disabled

                  return (
                    <button
                      key={`${group.potionType}-${group.rarity}`}
                      onClick={() => handlePotionToggle(group.potionType, group.rarity)}
                      disabled={!canSelect}
                      className={`
                        relative p-3 rounded-lg border-2 transition-all duration-200
                        ${isSelected 
                          ? 'border-purple-500 bg-purple-900/50 shadow-lg shadow-purple-500/25' 
                          : canSelect
                            ? 'border-slate-600 bg-slate-700 hover:border-slate-500 hover:bg-slate-600'
                            : 'border-slate-700 bg-slate-800 opacity-50 cursor-not-allowed'
                        }
                      `}
                    >
                      {/* AGC: Selection indicator */}
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                      
                      {/* AGC: Potion info */}
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <img 
                            src={potionInfo?.image || '/potion_red.png'} 
                            alt={potionInfo?.name || 'Potion'}
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <div className={`font-bold text-sm ${canSelect ? 'text-white' : 'text-slate-500'}`}>
                          {potionInfo?.name || `Potion ${group.potionType}`}
                        </div>
                        <div className={`text-xs ${canSelect ? 'text-slate-300' : 'text-slate-600'}`}>
                          {rarityName} ({group.magnitude}%)
                        </div>
                        <div className={`text-xs mt-1 ${canSelect ? 'text-slate-300' : 'text-slate-600'}`}>
                          Qty: {group.totalBalance}
                        </div>
                      </div>
                    </button>
                  )
                })
              })()
            )}
          </div>
        </div>
      )}

      {/* AGC: Friends tab placeholder */}
      {activeTab === 'FRIENDS' && (
        <div className="border border-slate-600 rounded-lg p-6 text-center mb-3">
          <div className="text-4xl mb-2">👥</div>
          <p className="text-slate-400">Friends coming soon</p>
        </div>
      )}

      {/* AGC: Selected items display */}
      {((activeTab === 'CLASSIC' && selectedBuffs.length > 0) || (activeTab === 'POTIONS' && selectedPotions.length > 0)) && (
        <div className="bg-slate-700 rounded-lg p-3 mb-3">
          <div className="text-white text-sm font-bold mb-2">Selected for Battle:</div>
          <div className="flex flex-wrap gap-2">
            {activeTab === 'CLASSIC' && selectedBuffs.map(buffId => {
              const buffInfo = BUFF_TYPES[buffId as keyof typeof BUFF_TYPES]
              return (
                <div key={buffId} className="bg-purple-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                  <img src={buffInfo.image} alt={buffInfo.name} className="w-4 h-4 object-contain" />
                  <span>{buffInfo.name}</span>
                </div>
              )
            })}
            {activeTab === 'POTIONS' && selectedPotions.map(tokenId => {
              const potion = ownedPotions.find(p => p.tokenId === tokenId)
              const potionInfo = potion ? POTION_TYPES[potion.potionType as keyof typeof POTION_TYPES] : null
              return (
                <div key={tokenId} className="bg-purple-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                  {potionInfo?.image && (
                    <img src={potionInfo.image} alt={potionInfo.name} className="w-4 h-4 object-contain" />
                  )}
                  <span>{potionInfo?.name || `Potion #${tokenId}`}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* AGC: Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleClearAll}
          disabled={disabled || (selectedBuffs.length === 0 && selectedPotions.length === 0)}
          className="flex-1 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 disabled:opacity-50 text-white py-2 px-3 rounded text-sm transition-colors"
        >
          Clear All
        </button>
        <div className="flex-1 text-center py-2 text-slate-400 text-xs">
          {activeTab === 'CLASSIC' && (
            selectedBuffs.length === 0 
              ? 'Select buffs to enhance your battle'
              : `${selectedBuffs.length} buff${selectedBuffs.length > 1 ? 's' : ''} ready!`
          )}
          {activeTab === 'POTIONS' && (
            selectedPotions.length === 0 
              ? 'Select 1 potion to enhance your battle'
              : '1 potion ready!'
          )}
          {activeTab === 'FRIENDS' && 'Friends coming soon'}
        </div>
      </div>

      {/* AGC: Item descriptions */}
      {activeTab === 'CLASSIC' && selectedBuffs.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-600">
          <div className="text-white text-sm font-bold mb-2">Buff Effects:</div>
          {selectedBuffs.map(buffId => {
            const buffInfo = BUFF_TYPES[buffId as keyof typeof BUFF_TYPES]
            return (
              <div key={buffId} className="text-slate-300 text-xs mb-1 flex items-center gap-1">
                <img src={buffInfo.image} alt={buffInfo.name} className="w-4 h-4 object-contain" />
                <span className="font-bold">{buffInfo.name}:</span> {buffInfo.description}
              </div>
            )
          })}
        </div>
      )}

      {activeTab === 'POTIONS' && selectedPotions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-600">
          <div className="text-white text-sm font-bold mb-2">Potion Effects:</div>
          {selectedPotions.map(tokenId => {
            const potion = ownedPotions.find(p => p.tokenId === tokenId)
            const potionInfo = potion ? POTION_TYPES[potion.potionType as keyof typeof POTION_TYPES] : null
            const rarityName = potion ? RARITY_NAMES[potion.rarity] : 'Unknown'
            return (
              <div key={tokenId} className="text-slate-300 text-xs mb-1 flex items-center gap-1">
                {potionInfo?.image && (
                  <img src={potionInfo.image} alt={potionInfo.name} className="w-4 h-4 object-contain" />
                )}
                <span className="font-bold">{potionInfo?.name || `Potion #${tokenId}`}:</span> 
                <span>{potionInfo?.description || 'Unknown effect'} ({rarityName}, {potion?.magnitude}%)</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
